/**
 * Lista de Bots por cliente — NOVO MODELO TILTI
 * Exibe os bots e gera o código completo do widget (suspenso e embedded) com o botId já inserido.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE } from '../config';
import StyledSelect from './StyledSelect';
import Modal from './ui/Modal';
import Button from './ui/Button';

// Nomes de marca para o cliente (Basic, Smart, Bolt); provedor/modelo reais são internos
const TIER_LABEL = { basico: 'Basic', intermediario: 'Smart', avancado: 'Bolt' };
const STATUS_LABEL = { active: 'Ativo', inactive: 'Inativo' };

const WIDGET_BASE = `${API_BASE}/widget`;

/** Gera o código HTML do chat suspenso (flutuante) com o botId inserido. Widget envia botId à API. */
function getSuspensoCode(botId) {
  return `<!-- Chat suspenso (flutuante) - Neurobot Tilti -->
<link rel="stylesheet" href="${WIDGET_BASE}/widget.css">
<script src="${WIDGET_BASE}/widget.js"></script>
<script>
document.addEventListener('DOMContentLoaded', function() {
  function init() {
    if (typeof window.Neurobot !== 'undefined') {
      new window.Neurobot({ botId: '${botId}' });
    } else {
      setTimeout(init, 200);
    }
  }
  init();
});
</script>`;
}

/** Gera o código HTML do widget embutido (embedded) com o botId inserido. */
function getEmbeddedCode(botId) {
  return `<!-- Widget embutido (embedded) - Neurobot Tilti -->
<link rel="stylesheet" href="${WIDGET_BASE}/widgetEmbedded.css">
<div id="neurobot-container"></div>
<script src="${WIDGET_BASE}/widgetEmbedded.js" defer></script>
<script>
document.addEventListener('DOMContentLoaded', function() {
  if (typeof initNeurobotEmbedded === 'function') {
    initNeurobotEmbedded('neurobot-container', '${botId}', {});
  } else {
    setTimeout(function() {
      initNeurobotEmbedded('neurobot-container', '${botId}', {});
    }, 300);
  }
});
</script>`;
}

const BotsList = () => {
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [bots, setBots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newBotName, setNewBotName] = useState('');
  const [newBotTier, setNewBotTier] = useState('avancado');
  const [creating, setCreating] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/clients`);
        const list = Array.isArray(res.data) ? res.data : [];
        setClients(list);
        if (list.length && !selectedClientId) {
          setSelectedClientId(list[0].id);
        }
      } catch (err) {
        console.error('Erro ao carregar clientes:', err);
        setError('Não foi possível carregar os clientes.');
      }
    };
    fetchClients();
  }, [selectedClientId]);

  useEffect(() => {
    if (!selectedClientId) {
      setBots([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError('');
    axios
      .get(`${API_BASE}/api/bots`, { params: { clientId: selectedClientId } })
      .then(res => {
        if (!cancelled) setBots(Array.isArray(res.data) ? res.data : []);
      })
      .catch(err => {
        if (!cancelled) {
          setError(err.response?.data?.error || 'Erro ao carregar bots.');
          setBots([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [selectedClientId]);

  const copyCode = (text, label) => {
    navigator.clipboard.writeText(text).then(() => {
      alert(`Código (${label}) copiado! Cole no HTML do seu site.`);
    }).catch(() => {
      prompt(`Copie o código (${label}):`, text);
    });
  };

  const handleCreateBot = async () => {
    const name = newBotName.trim();
    if (!name || !selectedClientId) {
      setError('Informe o nome do bot.');
      return;
    }
    setCreating(true);
    setError('');
    try {
      await axios.post(`${API_BASE}/api/bots`, { clientId: selectedClientId, name, tier: newBotTier });
      const res = await axios.get(`${API_BASE}/api/bots`, { params: { clientId: selectedClientId } });
      setBots(Array.isArray(res.data) ? res.data : []);
      setNewBotName('');
      setShowCreateForm(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao criar bot.');
    } finally {
      setCreating(false);
    }
  };

  const requestDeleteBot = (botId, botName) => {
    setPendingDelete({ id: botId, name: botName });
  };

  const confirmDeleteBot = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    setError('');
    try {
      await axios.delete(`${API_BASE}/api/bots/${pendingDelete.id}`);
      const res = await axios.get(`${API_BASE}/api/bots`, { params: { clientId: selectedClientId } });
      setBots(Array.isArray(res.data) ? res.data : []);
      setPendingDelete(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao excluir bot.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>Bots (agentes)</h2>
      <p style={styles.subtitle}>
        Escolha o cliente e o bot. Copie o código do <strong>chat suspenso</strong> ou do <strong>widget embutido</strong> e cole no HTML do seu site.
      </p>
      {/* Classe para sobrescrever o cyan global no bloco de código e manter legibilidade */}
      <style>{`.bots-code-block code { color: #E2E8F0 !important; background: transparent !important; }`}</style>

      {error && <div style={styles.error}>{error}</div>}

      <div style={styles.form}>
        <label style={styles.label}>Cliente:</label>
        <StyledSelect
          value={selectedClientId}
          onChange={setSelectedClientId}
          options={[
            { value: '', label: 'Selecione um cliente' },
            ...clients.map(c => ({ value: c.id, label: `${c.name} (${c.id})` }))
          ]}
          placeholder="Selecione um cliente"
        />
      </div>

      {loading && <p style={styles.loading}>Carregando bots...</p>}

      {!loading && selectedClientId && (
        <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <button type="button" onClick={() => setShowCreateForm(!showCreateForm)} style={styles.createBtn}>
            {showCreateForm ? 'Cancelar' : '+ Criar novo bot'}
          </button>
          {showCreateForm && (
            <div style={styles.createForm}>
              <input
                type="text"
                placeholder="Nome do bot"
                value={newBotName}
                onChange={e => setNewBotName(e.target.value)}
                style={styles.createInput}
              />
              <div style={{ minWidth: 180 }}>
                <StyledSelect
                  value={newBotTier}
                  onChange={setNewBotTier}
                  options={[
                    { value: 'avancado', label: 'Bolt (avançado)' },
                    { value: 'intermediario', label: 'Smart (intermediário)' },
                    { value: 'basico', label: 'Basic (básico)' },
                  ]}
                  placeholder="Tier"
                />
              </div>
              <button type="button" onClick={handleCreateBot} disabled={creating || !newBotName.trim()} style={styles.createSubmitBtn}>
                {creating ? 'Criando...' : 'Criar'}
              </button>
            </div>
          )}
        </div>
      )}

      {!loading && selectedClientId && bots.length === 0 && !showCreateForm && (
        <p style={styles.empty}>
          Nenhum bot para este cliente. Clique em &quot;Criar novo bot&quot; para adicionar.
        </p>
      )}

      {!loading && bots.length > 0 && bots.map(bot => (
        <div key={bot.id} style={styles.botCard}>
          <div style={styles.botHeader}>
            <div style={{ flex: 1 }}>
              <strong style={styles.botName}>{bot.name}</strong>
              <span style={styles.botMeta}>
                {' '}{TIER_LABEL[bot.tier] || bot.tier}
                {' · '}{STATUS_LABEL[bot.status] || bot.status} · <code style={styles.botId}>{bot.id}</code>
              </span>
            </div>
            <button
              type="button"
              onClick={() => requestDeleteBot(bot.id, bot.name)}
              style={styles.deleteBtn}
              title="Excluir bot"
            >
              Excluir
            </button>
          </div>

          <div style={styles.codeSection}>
            <div style={styles.codeBlock}>
              <div style={styles.codeTitle}>1. Chat suspenso (flutuante)</div>
              <p style={styles.codeDesc}>Ícone flutuante no canto da página; ao clicar abre o chat.</p>
              <pre style={styles.pre} className="bots-code-block"><code>{getSuspensoCode(bot.id)}</code></pre>
              <button type="button" onClick={() => copyCode(getSuspensoCode(bot.id), 'chat suspenso')} style={styles.copyBtn}>
                Copiar código
              </button>
            </div>

            <div style={styles.codeBlock}>
              <div style={styles.codeTitle}>2. Widget embutido (embedded)</div>
              <p style={styles.codeDesc}>Chat dentro de uma área da página (ex.: container na lateral ou no conteúdo).</p>
              <pre style={styles.pre} className="bots-code-block"><code>{getEmbeddedCode(bot.id)}</code></pre>
              <button type="button" onClick={() => copyCode(getEmbeddedCode(bot.id), 'widget embutido')} style={styles.copyBtn}>
                Copiar código
              </button>
            </div>
          </div>
        </div>
      ))}

      <Modal
        open={!!pendingDelete}
        onClose={() => !deleting && setPendingDelete(null)}
        title="Excluir bot?"
        closeOnBackdrop={!deleting}
        style={{ maxWidth: 480 }}
        footer={
          <div style={styles.modalFooter}>
            <button
              type="button"
              onClick={() => setPendingDelete(null)}
              disabled={deleting}
              style={styles.modalCancelBtn}
            >
              Cancelar
            </button>
            <Button
              type="button"
              onClick={confirmDeleteBot}
              disabled={deleting}
              style={styles.modalDangerBtn}
            >
              {deleting ? 'Excluindo...' : 'Excluir'}
            </Button>
          </div>
        }
      >
        {pendingDelete && (
          <p style={styles.modalBody}>
            Excluir o bot &quot;{pendingDelete.name}&quot;? As conversas e leads deste bot serão preservados, mas o bot será removido.
          </p>
        )}
      </Modal>

      {/* Vídeo de ajuda - Bots */}
      <section style={{ marginTop: 48, paddingTop: 24, borderTop: '1px solid rgba(0, 212, 255, 0.2)' }}>
        <h3 style={{ color: '#00D4FF', fontSize: '1rem', fontWeight: 600, marginBottom: 16, textTransform: 'uppercase', letterSpacing: 1 }}>Vídeo de ajuda</h3>
        <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden', maxWidth: 800, margin: '0 auto', borderRadius: 12, border: '1px solid rgba(0, 212, 255, 0.3)' }}>
          <iframe
            title="Como usar Bots"
            src="https://www.loom.com/embed/e14922b396794b1a8a7fe8058d6d00f9"
            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
            frameBorder="0"
            allow="fullscreen"
            allowFullScreen
          />
        </div>
      </section>
    </div>
  );
};

/* Tema alinhado ao dashboard: escuro, cyan/roxo, texto legível */
const styles = {
  container: {
    padding: '20px',
    maxWidth: '1000px',
    margin: '0 auto',
  },
  title: {
    fontSize: '1.8rem',
    marginBottom: '8px',
    color: '#FFFFFF',
    fontWeight: '700',
  },
  subtitle: {
    color: '#B0BEC5',
    marginBottom: '20px',
    fontSize: '14px',
  },
  error: {
    padding: '12px',
    background: 'rgba(248, 215, 218, 0.15)',
    color: '#f8a0a0',
    borderRadius: '8px',
    marginBottom: '16px',
    border: '1px solid rgba(248, 100, 100, 0.3)',
  },
  form: {
    marginBottom: '24px',
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontWeight: '600',
    color: '#B0BEC5',
  },
  select: {
    padding: '10px 14px',
    fontSize: '14px',
    borderRadius: '8px',
    border: '1px solid rgba(0, 212, 255, 0.3)',
    background: 'rgba(10, 14, 39, 0.6)',
    color: '#E2E8F0',
    minWidth: '280px',
  },
  loading: {
    color: '#B0BEC5',
    fontStyle: 'italic',
  },
  empty: {
    padding: '24px',
    color: '#B0BEC5',
    textAlign: 'center',
  },
  botCard: {
    marginBottom: '32px',
    border: '1px solid rgba(0, 212, 255, 0.25)',
    borderRadius: '12px',
    overflow: 'hidden',
    background: 'rgba(10, 14, 39, 0.6)',
    backdropFilter: 'blur(10px)',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), inset 0 0 20px rgba(0, 212, 255, 0.06)',
  },
  botHeader: {
    padding: '14px 20px',
    background: 'rgba(0, 212, 255, 0.08)',
    borderBottom: '1px solid rgba(0, 212, 255, 0.2)',
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '10px',
  },
  botName: {
    fontSize: '1.1rem',
    color: '#FFFFFF',
  },
  botMeta: {
    fontSize: '13px',
    color: '#B0BEC5',
  },
  botId: {
    fontSize: '11px',
    background: 'rgba(0, 212, 255, 0.15)',
    color: '#E2E8F0',
    padding: '2px 8px',
    borderRadius: '4px',
    wordBreak: 'break-all',
  },
  codeSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    padding: '20px',
  },
  codeBlock: {
    border: '1px solid rgba(0, 212, 255, 0.2)',
    borderRadius: '8px',
    overflow: 'hidden',
    background: 'rgba(5, 7, 16, 0.8)',
  },
  codeTitle: {
    padding: '10px 14px',
    background: 'rgba(176, 38, 255, 0.15)',
    fontSize: '14px',
    fontWeight: '600',
    color: '#E2E8F0',
    borderBottom: '1px solid rgba(0, 212, 255, 0.15)',
  },
  codeDesc: {
    margin: '0',
    padding: '8px 14px',
    fontSize: '13px',
    color: '#B0BEC5',
  },
  pre: {
    margin: '0',
    padding: '14px',
    background: '#0f172a',
    color: '#E2E8F0',
    fontSize: '12px',
    lineHeight: '1.5',
    overflow: 'auto',
    maxHeight: '280px',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
  },
  copyBtn: {
    margin: '0 14px 14px',
    padding: '8px 16px',
    background: 'linear-gradient(135deg, #B026FF 0%, #0066FF 100%)',
    color: '#fff',
    border: '1px solid rgba(0, 212, 255, 0.4)',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '600',
    boxShadow: '0 0 15px rgba(176, 38, 255, 0.3)',
  },
  createBtn: {
    padding: '10px 18px',
    background: 'linear-gradient(135deg, #00D4FF 0%, #B026FF 100%)',
    color: '#fff',
    border: '1px solid rgba(0, 212, 255, 0.4)',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
  },
  createForm: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap',
  },
  createInput: {
    padding: '8px 12px',
    borderRadius: '6px',
    border: '1px solid rgba(0, 212, 255, 0.3)',
    background: 'rgba(10, 14, 39, 0.8)',
    color: '#E2E8F0',
    fontSize: '14px',
    minWidth: '180px',
  },
  createSubmitBtn: {
    padding: '8px 16px',
    background: 'rgba(0, 212, 255, 0.3)',
    color: '#fff',
    border: '1px solid rgba(0, 212, 255, 0.5)',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '600',
  },
  deleteBtn: {
    padding: '6px 12px',
    background: 'rgba(239, 68, 68, 0.2)',
    color: '#f87171',
    border: '1px solid rgba(239, 68, 68, 0.4)',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: '500',
  },
  modalBody: {
    color: '#B0BEC5',
    fontSize: '0.95rem',
    lineHeight: 1.5,
    margin: 0,
  },
  modalFooter: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 12,
    justifyContent: 'flex-end',
    marginTop: 24,
  },
  modalCancelBtn: {
    padding: '12px 18px',
    background: 'transparent',
    color: '#B0BEC5',
    border: '1px solid rgba(0, 212, 255, 0.35)',
    borderRadius: 8,
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 600,
  },
  modalDangerBtn: {
    background: 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)',
    border: '1px solid rgba(248, 113, 113, 0.5)',
  },
};

export default BotsList;
